import { withErrorBoundary } from 'app/components/error-boundary';
import { PromoPagePlaceholder } from 'app/components/promo-page-placeholder';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useUser } from 'app/network/user';
import type { NavigationScreen } from 'app/types/navigation';
import { Trans } from 'react-i18next';
import { useScopedTranslation } from 'app/hooks/i18n';
import React from 'react';
import { useAllListings } from 'app/network/listing';
import { Button } from 'app/design/primitives/button';
import { useNavigation } from 'app/hooks/navigation';
import { TrialPromoOfferMissing } from 'app/design/icons/trial-promo-offer-missing';
import { TrialPromoOfferCreated } from 'app/design/icons/trial-promo-offer-created';
import { ListingType } from 'app/types/listing';
import { useOpenOffer } from 'app/hooks/use-open-offer';

const TrialPromoScreenComponent: NavigationScreen<'trialPromo'> = () => {
  const navigation = useNavigation<'trialPromo'>();
  const { t } = useScopedTranslation();

  const { data: user } = useUser();
  const { data: listings } = useAllListings(
    {
      type: ListingType.offer,
      userId: user?.id,
      isLoggedIn: true,
    },
    'TrialPromo',
    {
      throwOnError: true,
      enabled: !!user?.id,
    },
  );
  const offersCreated = listings?.pages[0]?.results ?? [];

  const openOffer = useOpenOffer();
  const goToNew = () => {
    navigation.goBack();
    setTimeout(
      () =>
        openOffer(navigation, {
          defaultValues: { type: ListingType.offer },
          newListingSource: 'trial_offer',
        }),
      10,
    );
  };

  const showPlaceholder = !user || !listings;
  if (showPlaceholder) {
    return <PromoPagePlaceholder />;
  }

  return (
    <Page>
      <Column gap="spacing.general.x4" paddingHorizontal="spacing.general.x8">
        <Text
          fontWeight="700"
          marginBottom="spacing.general.x4"
          marginTop="spacing.general.x4"
          textAlign="center"
          variant="headerXSmall"
        >
          {t('title')}
        </Text>

        <Text
          fontSize={18}
          lineHeight={22}
          marginHorizontal="spacing.general.x2"
          marginTop="spacing.general.x4"
          textAlign="center"
        >
          <Trans components={{ strong: <Text fontWeight="700" /> }} i18nKey="details" ns="trialPromo" />
        </Text>

        <Button marginVertical="spacing.general.x4" onPress={goToNew} variant="primary">
          {t('primaryButtonText')}
        </Button>

        <Row
          alignItems="center"
          alignSelf="center"
          flexWrap="wrap"
          gap="spacing.general.x2"
          style={{ justifyContent: 'center' }}
        >
          {offersCreated.map((offer) => (
            <TrialPromoOfferCreated key={offer.id} />
          ))}
          {offersCreated.length < 1 ? <TrialPromoOfferMissing /> : undefined}
          {offersCreated.length < 2 ? <TrialPromoOfferMissing /> : undefined}
          {offersCreated.length < 3 ? <TrialPromoOfferMissing /> : undefined}
          {offersCreated.length < 4 ? <TrialPromoOfferMissing /> : undefined}
          {offersCreated.length < 5 ? <TrialPromoOfferMissing /> : undefined}
        </Row>

        <Text fontSize={18} lineHeight={22} marginHorizontal="spacing.general.x2" textAlign="center">
          {t(`numCreated${offersCreated.length <= 1 ? offersCreated.length.toString() : ''}`, {
            numOffersCreated: offersCreated.length,
          })}
        </Text>
      </Column>
    </Page>
  );
};

export const TrialPromoScreen: NavigationScreen<'trialPromo'> = withErrorBoundary(TrialPromoScreenComponent);
